/**
 * Permissions Page
 * Phase 5: RBAC & Permission System - M3
 * 
 * System admin permission management page
 * Features: DataGrid, search, filters, CRUD operations
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Button,
  Paper,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import PermissionTable from '../../components/admin/PermissionTable';
import PermissionFormModal from '../../components/admin/PermissionFormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import PermissionButton from '../../components/common/PermissionButton';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import { fetchPermissions, deletePermission } from '../../redux/slices/permissionSlice';

const PermissionsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { permissions, isLoading, error } = useSelector((state) => state.permission);
  
  // Permission-based guard
  const hasPermission = useHasPermission('permissions.manage');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // '', 'page', 'action', 'category'
  const [activeFilter, setActiveFilter] = useState('active'); // 'all', 'active', 'passive'
  const [menuFilter, setMenuFilter] = useState(''); // '', 'mainMenu', 'notMainMenu'
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPermission, setDeletingPermission] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Fetch permissions on mount and when filters change
  useEffect(() => {
    if (hasPermission) {
      const filters = {};
      
      if (debouncedSearch) {
        filters.search = debouncedSearch;
      }
      
      if (typeFilter === 'page') {
        filters.isPage = true;
      } else if (typeFilter === 'action') {
        filters.isPage = false;
      }
      
      // Default: only active permissions
      if (activeFilter === 'active') {
        filters.isActive = true;
      } else if (activeFilter === 'passive') {
        filters.isActive = false;
      }
      // 'all' means no filter
      
      dispatch(fetchPermissions(filters));
    }
  }, [dispatch, debouncedSearch, typeFilter, activeFilter, hasPermission]);
  
  // Filter permissions locally for immediate UI response
  const filteredPermissions = permissions.filter(p => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        p.code?.toLowerCase().includes(searchLower) ||
        p.name?.toLowerCase().includes(searchLower) ||
        p.path?.toLowerCase().includes(searchLower) ||
        p.description?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }
    
    // Type filter
    if (typeFilter === 'page' && !p.isPage) return false;
    if (typeFilter === 'category' && (p.isPage || !p.isMainMenu)) return false;
    if (typeFilter === 'action' && (p.isPage || p.isMainMenu)) return false;
    
    // Active filter
    if (activeFilter === 'active' && !p.isActive) return false;
    if (activeFilter === 'passive' && p.isActive) return false;
    
    // Menu filter
    if (menuFilter === 'mainMenu' && !p.isMainMenu) return false;
    if (menuFilter === 'notMainMenu' && p.isMainMenu) return false;
    
    return true;
  });
  
  // Pagination
  const paginatedPermissions = filteredPermissions.slice(
    (page - 1) * limit,
    page * limit
  );
  
  const handleEdit = (permission) => {
    setEditingPermission(permission);
    setModalOpen(true);
  };
  
  const handleDeleteClick = (permission) => {
    setDeletingPermission(permission);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    await dispatch(deletePermission(deletingPermission._id));
    setDeleteDialogOpen(false);
    setDeletingPermission(null);
  };
  
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingPermission(null);
  };
  
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  const handleRowsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page
  };
  
  // Permission-based guard
  if (!hasPermission) {
    return <ForbiddenPage />;
  }
  
  return (
    <>
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
          sx={{ mb: 3 }}
        >
          Geri Dön
        </Button>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Yetki Yönetimi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sistem genelindeki yetkileri yönetin
            </Typography>
          </Box>
          <PermissionButton
            permission="permissions.manage"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            Yeni Yetki
          </PermissionButton>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Search */}
            <TextField
              placeholder="Kod, isim, yol veya açıklama ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            
            {/* Active/Passive Filter */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={activeFilter}
                label="Durum"
                onChange={(e) => setActiveFilter(e.target.value)}
              >
                <MenuItem value="active">Sadece Aktifler</MenuItem>
                <MenuItem value="passive">Sadece Pasifler</MenuItem>
                <MenuItem value="all">Tümü</MenuItem>
              </Select>
            </FormControl>
            
            {/* Type Filter */}
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Yetki Tipi</InputLabel>
              <Select
                value={typeFilter}
                label="Yetki Tipi"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="page">Sadece Sayfalar</MenuItem>
                <MenuItem value="category">Sadece Kategoriler</MenuItem>
                <MenuItem value="action">Sadece Aksiyonlar</MenuItem>
              </Select>
            </FormControl>
            
            {/* Menu Filter */}
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Menü Görünümü</InputLabel>
              <Select
                value={menuFilter}
                label="Menü Görünümü"
                onChange={(e) => setMenuFilter(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="mainMenu">Ana Menüde Görünür</MenuItem>
                <MenuItem value="notMainMenu">Ana Menüde Gizli</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {/* Filter Info */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Aktif/Pasif:</strong> Pasif yetkiler soft-delete gibidir (silinmiş sayılır)
              {' • '}
              <strong>Sayfa:</strong> isPage=true, PATH var
              {' • '}
              <strong>Kategori:</strong> isPage=false, isMainMenu=true (grup başlığı)
              {' • '}
              <strong>Aksiyon:</strong> isPage=false, isMainMenu=false (buton kontrolü)
            </Typography>
          </Box>
        </Paper>
        
        {/* Permission Table */}
        <PermissionTable
          permissions={paginatedPermissions}
          total={filteredPermissions.length}
          page={page}
          limit={limit}
          loading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Container>
      
      {/* Modals */}
      <PermissionFormModal
        open={modalOpen}
        onClose={handleModalClose}
        permission={editingPermission}
      />
      
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Yetki Sil"
        message={`"${deletingPermission?.name}" (${deletingPermission?.code}) yetkisini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        severity="error"
      />
    </>
  );
};

export default PermissionsPage;

